// ============================================================================
//  ClaimMedications — the Medications table inside the inline claim editor
// ----------------------------------------------------------------------------
//  The fourth table with the same rhythm: Add a row, fill it in, save it, Add the
//  next. What a medication needs beyond the others is a PRESCRIPTION, so the row
//  has more to fill in:
//
//    MEDICATION  the searchable field, from store_pro. Keyed on the NAME: the name
//                repeats within a scheme and 239 of the 1,199 rows have no code.
//                Picking it brings the code, the dosage form, the strength and the
//                unit price.
//    Rx          dose / frequency / duration / route — typed, because they are the
//                prescription and no catalogue holds them. Shown as the same
//                "625 mg x 1 x 12 HOURLY x 7 DAYS" line the imported claims use.
//    QTY         typed. It drives the money.
//    TOTAL       qty x unit price, COMPUTED and never typed — a total that
//                disagrees with its own quantity is what gets a batch queried.
//    DATE        the visit's own days, as everywhere else.
// ============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { cachedGet } from './catalogueCache';
import { Save, Trash2, X, Loader2, Pencil } from 'lucide-react';
import {
  claimStoreMedicationCatalogue, claimStoreMedication, claimStoreProcedureDates,
  claimStoreMedicationPattern,
} from '../BaseURL';
import SearchSelect from './SearchSelect';
import VisitDateSelect from './VisitDateSelect';
import { confirmRemove } from './claimsDialog';

const BRAND = {
  green: '#1A9B7F', greenDark: '#137a63', greenSoft: '#e8f5f1',
  line: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkFaint: '#94a3b8',
  head: '#f1f5f9', danger: '#b91c1c', dangerSoft: '#fef2f2',
  amber: '#b45309', amberSoft: '#fffbeb',
};

const th = {
  textAlign: 'left', padding: '9px 10px', fontSize: 10.5, fontWeight: 800,
  letterSpacing: 0.4, textTransform: 'uppercase', color: BRAND.inkSoft,
  background: BRAND.head, borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
};
const td = {
  padding: '8px 10px', fontSize: 12, color: BRAND.ink,
  borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'middle',
};
const cell = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12,
  border: `1px solid ${BRAND.green}`, borderRadius: 7, background: '#fff',
  color: BRAND.ink, outline: 'none', fontFamily: 'inherit',
};
//  `off` styles a DISABLED button. Without it a blocked Save still looked
//  clickable, which reads as the button being broken rather than the row being
//  incomplete.
const iconBtn = (tone, off) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 7,
  cursor: off ? 'not-allowed' : 'pointer',
  opacity: off ? 0.45 : 1,
  border: `1px solid ${tone === 'danger' ? '#fecaca' : BRAND.line}`,
  background: tone === 'danger' ? BRAND.dangerSoft : '#fff',
  color: tone === 'danger' ? BRAND.danger : BRAND.greenDark,
});

//  `dmy` lived here and in the two sibling tables, three identical copies. Every
//  call site was a date cell, and those are now VisitDateSelect — which formats its
//  own value — so the copies are gone rather than left unused.
const ghs = (v) => `GHS ${Number(v || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

//  The prescription, read back as one line — the shape the imported claims use.
const rxLine = (m) => [m.dose, m.frequency, m.duration].filter(Boolean).join(' x ')
  + (m.route ? ` · ${m.route}` : '');

//  NHIA validates the XML on the dose unit, so a row without one is a rejected
//  claim. The chip says both things at once: what it is, and whether it was
//  RECORDED against the drug or read off its name — a fact and an inference should
//  not look the same to someone signing the claim off.
//  ── A NUMBER IS NOT A DOSE UNIT, AND MUST NOT LOOK LIKE ONE ─────────────────
//
//  REPORTED off a live claim whose chips read 1, 180, 250, 5 — quantities typed
//  into the dose unit box. Every one rendered GREEN, the colour this chip uses
//  for "recorded against the drug in the catalogue", so a value NHIA rejects
//  outright was the most confident thing in the row.
//
//  The backend now reads the unit off the medication's own name wherever it can,
//  so most of these arrive as a real unit marked `from: 'name'` and show amber.
//  What is left is the genuinely unrescuable: a number, with nothing in the name
//  to replace it. Those are shown for what they are — the bad value, in red, so
//  it can be corrected rather than trusted.
const doseChip = (value, from) => {
  //  Same shape test the server enforces on save. A real unit has already been
  //  put into "quantity space unit" form before it gets here, so anything still
  //  failing this carries no usable unit.
  const usable = value != null && String(value).trim() !== ''
    && DOSE_UNIT_RE.test(String(value).trim());
  if (!usable) {
    const shown = String(value == null ? '' : value).trim();
    return (
      <span title={shown
        ? `"${shown}" is not a dose unit — NHIA wants a quantity, a space, then `
          + 'the unit, like "500 mg". This line would be rejected.'
        : 'NHIA requires a dose unit — this line would be rejected'}
        style={{
          padding: '1px 6px', borderRadius: 4, fontSize: 9.5, fontWeight: 800,
          background: BRAND.dangerSoft, color: BRAND.danger,
        }}>{shown ? `${shown} · not a unit` : 'no dose unit'}</span>
    );
  }
  const inferred = from === 'name';
  return (
    <span
      title={inferred
        ? 'Read from the medication name — not recorded against the drug'
        : 'Recorded against the drug in the catalogue'}
      style={{
        padding: '1px 6px', borderRadius: 4, fontSize: 9.5, fontWeight: 800,
        background: inferred ? BRAND.amberSoft : BRAND.greenSoft,
        color: inferred ? BRAND.amber : BRAND.greenDark,
        border: inferred ? '1px dashed #fcd34d' : '1px solid transparent',
      }}>{value}{inferred ? ' ·  from name' : ''}</span>
  );
};

//  The NHIA dose unit shape: a quantity, a space, then the unit — "500 mg".
//  Mirrors DOSE_UNIT_RE in claimStore.js, which enforces it on save.
const DOSE_UNIT_RE = /^\d+(?:[.,]\d+)?\s+\S.*$/;

//  EVERY prescription field is required. A medication line that reaches NHIA
//  without one of these is rejected, and a half-filled row saved now is a rejection
//  discovered at submission — when the error names the batch and not the drug.
//  Multi Vet enforces the same list, so the two paths cannot disagree.
const RX_REQUIRED = [
  ['dose_unit', 'dose unit'],
  ['dose',      'dose'],
  ['frequency', 'frequency'],
  ['duration',  'duration'],
  ['route',     'route'],
];

//  What is still missing on a row, in the order the fields appear on screen.
//  Quantity is checked as a NUMBER — "0" is filled in but not valid.
const missingOf = (row) => {
  const out = RX_REQUIRED
    .filter(([k]) => !String(row?.[k] ?? '').trim())
    .map(([, label]) => label);
  if (!(Number(row?.quantity) > 0)) out.push('quantity');
  return out;
};

//  A dose unit that is present but not NHIA-shaped. Reported separately from
//  "missing", because the fix is different: retype it, do not fill it in.
const badDoseUnit = (row) => {
  const v = String(row?.dose_unit ?? '').trim();
  return !!v && !DOSE_UNIT_RE.test(v);
};

const blankRow = (firstDate) => ({
  id: null, code: '', generic_name: '', dosage_form: '', strength: '',
  //  Separate from `strength`: strength describes the product ("5% in 0.9%
  //  (500 mL)"), dose_unit is the field NHIA validates ("500 mg").
  dose_unit: '',
  dose: '', frequency: '', duration: '', route: '',
  quantity: '', unit_price: 0, dispensed_at: firstDate || '',
});

export default function ClaimMedications({
  claimId, source, scheme, rows = [], onSaved, readOnly,
  //  The patient's age, so a learned prescription is one given to somebody of the
  //  same age group. The NHIS boundary is 12 years.
  age,
  addSignal = false, onAddConsumed,
}) {
  const [catalogue, setCatalogue] = useState([]);
  //  The controlled vocabularies come WITH the catalogue, so the screen and the
  //  store cannot disagree about what a frequency is.
  const [frequencies, setFrequencies] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [catNote, setCatNote] = useState(null);
  const [dates, setDates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    cachedGet(claimStoreMedicationCatalogue, { scheme })
      .then((data) => {
        if (!alive) return;
        setCatalogue(data?.options || []);
        setFrequencies(data?.frequencies || []);
        setRoutes(data?.routes || []);
        setCatNote(data?.note || null);
      })
      .catch(() => {
        if (!alive) return;
        setCatalogue([]);
        setCatNote('The medication list could not be loaded.');
      });
    return () => { alive = false; };
  }, [scheme]);

  const loadDates = useCallback(() => {
    if (!claimId) return;
    //  ttl: 0 — deduped against the other two tables asking in the same commit,
    //  never served from store. A save can extend the stay, and this is re-run
    //  after one; a stored answer would be the list from before it.
    cachedGet(claimStoreProcedureDates, { id: claimId, source }, { ttl: 0 })
      .then((data) => setDates(data?.dates || []))
      .catch(() => setDates([]));
  }, [claimId, source]);
  useEffect(loadDates, [loadDates]);

  const addRow = useCallback(() => {
    setError(null);
    setDrafts((d) => [...d, {
      key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ...blankRow(dates[0]),
    }]);
  }, [dates]);

  useEffect(() => {
    if (!addSignal) return;
    if (!readOnly) addRow();
    if (onAddConsumed) onAddConsumed();
  }, [addSignal, addRow, readOnly, onAddConsumed]);

  //  ── The quantity suggestion ───────────────────────────────────────────────
  //  doses per day x days x units per dose. Checked against the 6,616 imported
  //  rows that carry all three: it agrees with 4,544 of them.
  //
  //  It is a SUGGESTION and never an authority, because the other 2,072 are liquids
  //  and injections where the quantity is packs, not doses — a 7-day Carbocisteine
  //  course is 1 bottle, not 21 doses. So it fills the field, says it did, and stops
  //  the moment the officer types their own number.
  const perDayOf = (freqValue) => {
    const hit = frequencies.find((f) => f.value === freqValue);
    return hit ? hit.perDay : null;
  };
  const unitsPerDose = (dose) => {
    //  "500 mg x 2" — the number after the x is how many units make one dose.
    const m = String(dose || '').match(/x\s*([\d.]+)\s*$/i);
    return m ? Number(m[1]) : 1;
  };
  const suggestQuantity = (row) => {
    //  Only for a countable form. A syrup is dispensed as a bottle, so the
    //  arithmetic returns 21 where the answer is 1 — which is why the claims panel
    //  gates its Auto Calculate on the same rule.
    if (!row.countable) return null;
    const per = perDayOf(row.frequency);
    const days = parseInt(String(row.duration || '').replace(/[^\d]/g, ''), 10);
    if (!per || !Number.isFinite(days) || days <= 0) return null;
    //  Rounded UP, as the claims panel does: half a tablet cannot be dispensed.
    const q = Math.ceil(unitsPerDose(row.dose) * per * days);
    return Number.isFinite(q) && q > 0 ? String(q) : null;
  };

  //  Re-suggests after any change to the three inputs it depends on, unless the
  //  officer has taken the field over.
  const patch = (key, k, v) => setDrafts((d) => d.map((x) => {
    if (x.key !== key) return x;
    const next = { ...x, [k]: v };
    if (k === 'quantity') {
      //  Typed by hand from here on. A suggestion that kept overwriting what the
      //  officer typed would be worse than no suggestion at all.
      return { ...next, quantityTyped: true };
    }
    if (['frequency', 'duration', 'dose'].includes(k) && !x.quantityTyped) {
      const s = suggestQuantity(next);
      if (s != null) return { ...next, quantity: s, quantityAuto: true };
    }
    return next;
  }));

  // ── Finding a saved row's drug in the catalogue ───────────────────────────
  //  By CODE, not by name. The claims come from the NHIA XML and the catalogue from
  //  the NHIS booklet import, and the two do not name a drug the same way:
  //  "Magnesium Trisilicate + Aluminium Hydroxide" against the booklet's own
  //  wording. Measured over every medication on file — 47 distinct (code, name)
  //  pairs across 6,632 rows:
  //
  //      found by NAME : 4 of 47
  //      found by CODE : 47 of 47
  //
  //  and the 446 offered codes are unique. So editing a saved row used to open with
  //  an empty "Select medication" for all but four drugs, and the officer had to
  //  find the medication again to change a frequency.
  //
  //  The name is kept as a fallback for a catalogue whose rows carry no code.
  const medId = (o) => String(o.code || '').toUpperCase() || o.name;
  const medValueOf = (row) => String(row.code || '').toUpperCase() || row.generic_name;

  //  The claim's own drug, offered as an option when the catalogue does not contain
  //  it at all — a code retired since the claim was made, or a scheme whose
  //  catalogue has since changed. Without it the field would read "Select
  //  medication" for a row that plainly has a drug on it.
  const optionsWith = (row) => {
    if (!row.generic_name) return catalogue;
    const want = medValueOf(row);
    if (catalogue.some((o) => medId(o) === want)) return catalogue;
    return [{
      code: row.code || null,
      name: row.generic_name,
      strength: row.strength || null,
      strength_from: row.strength_from || null,
      dosage_form: row.dosage_form || null,
      unit_price: row.unit_price ?? null,
      countable: !!row.countable,
      onThisClaim: true,
    }, ...catalogue];
  };

  //  Already on the claim? The code where there is one, the name otherwise — 239
  //  catalogue rows have none — and always the day, because the same drug dispensed
  //  on another day of a stay is a further dispensing.
  const duplicateOf = (key, row) => {
    const same = (a, b) => String(a || '') === String(b || '');
    const hit = (r) => (row.code
      ? same(r.code, row.code) : same(r.generic_name, row.generic_name))
      && same(r.dispensed_at, row.dispensed_at || dates[0]);
    //  A row being EDITED must not be compared against its own saved copy.
    //  Opening a saved medication clones it into a draft while the original
    //  stays in `rows`, so an unqualified `rows.some(hit)` always matched that
    //  original — same drug, same date — and every edit was rejected with
    //  "already on this claim". Changing a dose or a frequency was impossible;
    //  only brand-new rows could ever save.
    //
    //  `row.id` is present only on a saved row that is being edited, so the
    //  exclusion is exact and a genuine second row for the same drug is still
    //  caught.
    if (rows.some((r) => !(row.id && String(r.id) === String(row.id)) && hit(r))) return 'saved';
    return drafts.some((x) => x.key !== key && hit(x)) ? 'draft' : null;
  };

  //  ── What the last prescription of this drug looked like ──────────────────
  //  Fetched on picking, and applied only to the fields the officer has not set.
  //  It is real practice, not a rule, so it fills in and says where it came from.
  const applyPattern = async (key, drugName) => {
    if (!drugName) return;
    try {
      const { data } = await axios.get(claimStoreMedicationPattern,
        { params: { name: drugName, age } });
      if (!data?.found) {
        setDrafts((d) => d.map((x) => (x.key === key
          ? { ...x, patternNote: data?.note || null, patternFound: false } : x)));
        return;
      }
      setDrafts((d) => d.map((x) => {
        if (x.key !== key) return x;
        //  The dose STRING is rebuilt from this drug's own dose unit and the
        //  learned units-per-dose — the ward table stores "2", not "500 mg x 2".
        const units = data.unitsPerDose || 1;
        const next = {
          ...x,
          dose: x.strength ? `${x.strength} x ${units}` : x.dose,
          frequency: data.frequency || x.frequency,
          duration: data.duration || x.duration,
          route: data.route || x.route,
          patternNote: data.note,
          patternFound: true,
        };
        //  The learned quantity is used only when the arithmetic cannot be trusted
        //  — a syrup's "1 bottle" is exactly the case the formula gets wrong.
        if (!next.countable && data.quantity) {
          return { ...next, quantity: String(data.quantity), quantityAuto: false,
            quantityFromPattern: true };
        }
        const s = suggestQuantity(next);
        if (s != null) return { ...next, quantity: s, quantityAuto: true };
        if (data.quantity) {
          return { ...next, quantity: String(data.quantity), quantityFromPattern: true };
        }
        return next;
      }));
    } catch {
      /* a missing pattern is not an error — the row is still perfectly usable */
    }
  };

  const pickMedication = (key, _id, opt) => {
    if (!opt) return;
    const next = {
      generic_name: opt.name || '',
      code: opt.code || '',
      dosage_form: opt.dosage_form || '',
      //  The dose unit NHIA validates on, with its provenance carried alongside so
      //  the row can say whether it was recorded or read off the name.
      strength: opt.strength || '',
      strength_from: opt.strength_from || null,
      //  Prefilled ONLY when the catalogue's value already satisfies the NHIA shape
      //  — the server sends null otherwise (339 store_pro rows are blank and 42
      //  hold a bare "1"), so a malformed unit is never silently carried onto a
      //  claim. Blank means it has to be typed.
      dose_unit: opt.dose_unit || '',
      //  The DOSE starts as the dose unit, one per dose — "500 mg x 1", the shape
      //  every imported row uses. The officer changes the multiplier when a dose is
      //  two tablets, and the quantity follows.
      dose: opt.strength ? `${opt.strength} x 1` : '',
      //  Whether the quantity is arithmetic for this drug. A tablet's quantity IS
      //  dose x times a day x days; a syrup's is a bottle, and the same arithmetic
      //  returns 21 where the answer is 1. The claims panel's rule, kept.
      countable: !!opt.countable,
      quantityAuto: false, quantityTyped: false, quantityFromPattern: false,
      patternNote: null, patternFound: false,
      //  From the catalogue, never typed — the money has to trace to a price
      //  somebody published.
      unit_price: opt.unit_price ?? 0,
    };
    const row = drafts.find((x) => x.key === key);
    const dup = duplicateOf(key, { ...row, ...next });
    setError(dup
      ? `${opt.name} is already on this claim`
        + `${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`
      : null);
    setDrafts((d) => d.map((x) => (x.key === key
      ? { ...x, ...next, duplicate: Boolean(dup) } : x)));
    //  Then fill the prescription in from the last time this drug was given.
    applyPattern(key, opt.name);
  };

  const drop = (key) => setDrafts((d) => d.filter((x) => x.key !== key));

  const editRow = (r) => {
    setError(null);
    setDrafts((d) => (d.some((x) => x.id === r.id) ? d : [...d, {
      key: `row-${r.id}`,
      ...r,
      //  The dose unit AS RESOLVED, not only as stored. A row whose strength was
      //  never recorded but was read off its name opens with it filled in, so
      //  saving turns the inference into recorded data — which is the point of
      //  being able to edit rather than delete and re-enter.
      strength: r.strength || r.dose_unit || '',
      strength_from: r.dose_unit_from || null,
      //  Carried over ONLY if it already satisfies the NHIA shape. claimDetail
      //  falls back to a value derived from the name, and some of those are things
      //  like a bare "1" — prefilling one would make an unrelated edit (changing a
      //  quantity) fail validation on save. Blank instead, so it is fixed on
      //  purpose rather than blocking the row.
      dose_unit: DOSE_UNIT_RE.test(String(r.dose_unit || '').trim())
        ? r.dose_unit : '',
      //  The quantity on a saved row was already decided by somebody. Editing the
      //  frequency must not silently overwrite it.
      quantityTyped: true,
    }]));
  };

  const save = async (row) => {
    if (!row.generic_name) { setError('Pick a medication first.'); return; }
    //  Named individually rather than "fill in all fields" — the officer should not
    //  have to hunt the form for which box is empty.
    const missing = missingOf(row);
    if (missing.length) {
      setError(missing.length === 1
        ? `The ${missing[0]} is required.`
        : `Still needed: ${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}.`);
      return;
    }
    if (badDoseUnit(row)) {
      setError('The dose unit needs a quantity, a space, then the unit — “500 mg”.');
      return;
    }
    const qty = Number(row.quantity);
    const dup = duplicateOf(row.key, row);
    if (dup) {
      setError(`${row.generic_name} is already on this claim`
        + `${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`);
      return;
    }
    setBusy(row.key); setError(null);
    try {
      const { data } = await axios[row.id ? 'put' : 'post'](claimStoreMedication, {
        id: row.id || undefined,
        code: row.code || null,
        generic_name: row.generic_name,
        dosage_form: row.dosage_form || null,
        strength: row.strength || null,
        //  The NHIA-validated dose unit, stored on the line (migration 044). The
        //  store refuses anything that is not a quantity, a space, then the unit.
        dose_unit: row.dose_unit || null,
        dose: row.dose || null,
        frequency: row.frequency || null,
        duration: row.duration || null,
        route: row.route || null,
        quantity: qty,
        unit_price: row.unit_price,
        dispensed_at: row.dispensed_at || dates[0] || null,
      }, { params: { id: claimId, source } });
      drop(row.key);
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save the medication.');
    } finally { setBusy(null); }
  };

  //  ── Save every finished draft in one go ───────────────────────────────────
  //  Ten drugs meant ten trips to ten separate save buttons. This saves the ones
  //  that are COMPLETE and leaves the rest on screen — a bulk action that silently
  //  dropped half-filled rows would be worse than the ten clicks.
  //
  //  Sequential, not Promise.all: each save is its own transaction and refuses a
  //  duplicate of a drug already on this claim for that day. Firing them together
  //  would race that check, and two identical rows would both pass it.
  const saveAll = async () => {
    const ready = drafts.filter((d) => d.generic_name
      && !missingOf(d).length && !badDoseUnit(d));
    if (!ready.length) return;

    setBusy('ALL'); setError(null);
    let saved = 0;
    const failed = [];
    for (const row of ready) {
      try {
        const qty = Number(row.quantity);
        const { data } = await axios[row.id ? 'put' : 'post'](claimStoreMedication, {
          id: row.id || undefined,
          code: row.code || null,
          generic_name: row.generic_name,
          dosage_form: row.dosage_form || null,
          strength: row.strength || null,
          dose_unit: row.dose_unit || null,
          dose: row.dose || null,
          frequency: row.frequency || null,
          duration: row.duration || null,
          route: row.route || null,
          quantity: qty,
          unit_price: row.unit_price,
          dispensed_at: row.dispensed_at || dates[0] || null,
        }, { params: { id: claimId, source } });
        drop(row.key);
        saved += 1;
        if (data && onSaved) onSaved(data);
      } catch (e) {
        //  Recorded and carried on: one rejected drug must not strand the nine
        //  behind it. The row stays on screen with the reason.
        failed.push(`${row.generic_name}: ${e?.response?.data?.message || 'could not be saved'}`);
      }
    }
    setBusy(null);

    const leftover = drafts.length - ready.length;
    const notes = [];
    if (failed.length) notes.push(failed.join(' · '));
    if (leftover) {
      notes.push(`${leftover} row(s) left — still missing required fields.`);
    }
    setError(notes.length ? notes.join(' ') : null);
    if (saved && !notes.length) setError(null);
  };

  const remove = async (r) => {
    // eslint-disable-next-line no-alert
    if (!(await confirmRemove({ what: 'medication', name: r.generic_name }))) return;
    setBusy(`row-${r.id}`); setError(null);
    try {
      const { data } = await axios.delete(claimStoreMedication,
        { params: { id: claimId, source, rowId: r.id } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not remove the medication.');
    } finally { setBusy(null); }
  };

  //  One draft row. A FUNCTION rather than inline JSX because it renders in
  //  two places: a new row at the top of the table, an edited row in the
  //  position of the row it replaces. Clicking Edit must not move anything.
  //  Computed once per row rather than at each use: Save reads it, its tooltip
  //  reads it, and the hint under the form reads it.
  const draftTr = (row, i) => {
    const rowMissing = missingOf(row);
    const rowBadUnit = badDoseUnit(row);
    const rowBlocked = rowMissing.length > 0 || rowBadUnit;
    return (
                <tr key={row.key} style={{
                  background: row.duplicate ? BRAND.dangerSoft : BRAND.greenSoft }}>
                  <td style={{ ...td, color: BRAND.inkFaint, verticalAlign: 'top',
                    paddingTop: 12 }}>{saved.length + i + 1}</td>
                  {/*  ONE spanning cell. A draft is a form, and forcing it into the
                       saved rows' column widths is what made the quantity and the
                       days boxes too small to read. */}
                  <td colSpan={6} style={{ ...td, padding: '10px 10px 12px' }}>

                    {/*  Line 1 — the medication, on its own full-width line. */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SearchSelect
                          options={optionsWith(row)}
                          value={medValueOf(row)}
                          idOf={medId}
                          onChange={(id, opt) => pickMedication(row.key, id, opt)}
                          placeholder="Select medication"
                          searchPlaceholder="Type a drug name or code…"
                          //  A row ADDED opens its list with the cursor already in
                          //  the search box. This row has several pickers — dose
                          //  unit, frequency, route — and only the DRUG one opens,
                          //  because the drug is what the others follow from.
                          autoOpen={!row.id}
                          ariaLabel="Medication"
                          triggerOf={(o) => <span>{o.name}</span>}
                          primaryOf={(o) => (
                            <span>
                              {o.name}
                              <span style={{ marginLeft: 6 }}>
                                {doseChip(o.strength, o.strength_from)}
                              </span>
                            </span>
                          )}
                          secondaryOf={(o) => [
                            o.code || 'no code',
                            //  Marked, because it is not from the catalogue: it is
                            //  what this claim already records.
                            o.onThisClaim ? 'already on this claim' : null,
                            o.dosage_form,
                            o.countable ? null : 'dispensed as packs',
                            o.unit_price ? `${ghs(o.unit_price)} each` : null,
                          ].filter(Boolean).join('  ·  ')} />
                      </div>
                      {/*  Save and discard sit with the medication, at the top, so
                           they do not move as the form grows. */}
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        <button type="button" style={iconBtn(null, rowBlocked)}
                          aria-label="Save medication"
                          //  Unreachable until the line is complete, and the tooltip
                          //  names the empty fields so the button explains itself
                          //  rather than just refusing.
                          title={rowBadUnit
                            ? 'The dose unit needs a quantity, a space, then the unit'
                            : rowMissing.length ? `Still needed: ${rowMissing.join(', ')}` : 'Save'}
                          disabled={busy === row.key || rowBlocked}
                          onClick={() => save(row)}>
                          {busy === row.key
                            ? <Loader2 size={13} className="spin" />
                            : <Save size={13} />}
                        </button>
                        <button type="button" title="Discard" style={iconBtn('danger')}
                          aria-label="Discard row"
                          onClick={() => drop(row.key)}><X size={13} /></button>
                      </div>
                    </div>

                    {/*  What the drug brings with it: the code and the dose unit NHIA
                         validates on, and where the prescription was learned from. */}
                    {row.generic_name && (
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap',
                        alignItems: 'center', marginTop: 5, fontSize: 10.5,
                        color: BRAND.inkFaint }}>
                        <span>
                          Code:{' '}
                          <strong style={{ color: row.code ? BRAND.ink : BRAND.inkFaint }}>
                            {row.code || 'none'}
                          </strong>
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center',
                          gap: 4 }}>
                          Dose unit: {doseChip(row.strength, row.strength_from)}
                        </span>
                        {!row.countable && (
                          <span style={{ color: BRAND.amber }}>
                            dispensed as packs — the quantity is not calculated
                          </span>
                        )}
                      </div>
                    )}
                    {row.patternNote && (
                      <div style={{ marginTop: 4, fontSize: 10.5, lineHeight: 1.4,
                        color: row.patternFound ? BRAND.greenDark : BRAND.inkFaint }}>
                        {row.patternFound ? '\u21BA ' : ''}{row.patternNote}
                      </div>
                    )}

                    {/*  Line 2 — the prescription, in three equal parts. */}
                    <div style={{ display: 'grid', gap: 8, marginTop: 10,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                      {/*  The NHIA-validated field. Refused on save unless it is a
                           quantity, a space, then the unit, so the box says so and
                           marks itself while it is wrong. */}
                      {field('Dose unit', (
                        <div>
                          <input
                            style={{
                              ...box,
                              borderColor: row.dose_unit
                                && !DOSE_UNIT_RE.test(String(row.dose_unit).trim())
                                ? BRAND.warning : undefined,
                            }}
                            value={row.dose_unit || ''} aria-label="Dose unit"
                            placeholder="e.g. 500 mg"
                            onChange={(e) => patch(row.key, 'dose_unit', e.target.value)} />
                          {row.dose_unit
                            && !DOSE_UNIT_RE.test(String(row.dose_unit).trim()) && (
                            <div style={{ fontSize: 10, color: '#8A5510', marginTop: 3 }}>
                              A quantity, a space, then the unit — “500 mg”.
                            </div>
                          )}
                        </div>
                      ))}
                      {field('Dose', (
                        <input style={box} value={row.dose || ''} aria-label="Dose"
                          placeholder="e.g. 500 mg x 1"
                          onChange={(e) => patch(row.key, 'dose', e.target.value)} />
                      ))}
                      {field('Frequency', (
                        <select style={box} value={row.frequency || ''}
                          aria-label="Frequency"
                          onChange={(e) => patch(row.key, 'frequency', e.target.value)}>
                          <option value="">Select frequency</option>
                          {frequencies.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ))}
                      {field('Duration', (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input style={{ ...box, width: 90 }} type="number" min="1"
                            step="1" aria-label="Duration in days"
                            value={String(row.duration || '').replace(/[^\d]/g, '')}
                            placeholder="0"
                            onChange={(e) => patch(row.key, 'duration',
                              e.target.value ? `${e.target.value} DAYS` : '')} />
                          <span style={{ fontSize: 11.5, color: BRAND.inkSoft }}>days</span>
                        </div>
                      ))}
                    </div>

                    {/*  Line 3 — route, then the quantity on a line where it can
                         actually be read, with the money beside it. */}
                    <div style={{ display: 'grid', gap: 8, marginTop: 8,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                      {field('Route', (
                        <select style={box} value={row.route || ''} aria-label="Route"
                          onChange={(e) => patch(row.key, 'route', e.target.value)}>
                          <option value="">Select route</option>
                          {routes.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ))}
                      {field('Quantity', (
                        <div>
                          <input style={{ ...box, fontWeight: 700, fontSize: 14 }}
                            type="number" min="0" step="any" value={row.quantity}
                            placeholder="0" aria-label="Quantity"
                            onChange={(e) => patch(row.key, 'quantity', e.target.value)} />
                          {row.quantityAuto && !row.quantityTyped && (
                            <div style={{ fontSize: 10, color: BRAND.greenDark,
                              marginTop: 3 }}>
                              auto · {unitsPerDose(row.dose)} × {perDayOf(row.frequency)}/day
                              × {String(row.duration || '').replace(/[^\d]/g, '')} days
                            </div>
                          )}
                          {row.quantityFromPattern && !row.quantityTyped && (
                            <div style={{ fontSize: 10, color: BRAND.inkFaint,
                              marginTop: 3 }}>from the last prescription</div>
                          )}
                          {row.generic_name && !row.countable && !row.quantityTyped
                            && !row.quantityFromPattern && (
                            <div style={{ fontSize: 10, color: BRAND.amber,
                              marginTop: 3 }}>enter the number of packs</div>
                          )}
                        </div>
                      ))}
                      {field('Total', (
                        <div style={{ ...box, background: '#f8fafc',
                          borderColor: BRAND.line, fontWeight: 800, fontSize: 14,
                          color: BRAND.ink }}>
                          {ghs(Number(row.unit_price || 0) * Number(row.quantity || 0))}
                          <div style={{ fontSize: 10, fontWeight: 400,
                            color: BRAND.inkFaint }}>
                            {ghs(row.unit_price)} each
                          </div>
                        </div>
                      ))}
                      {field('Date', dateCell(row))}
                    </div>

                    {/*  Names what is still empty, next to the fields rather than
                         only in the Save tooltip — a disabled button with no
                         explanation is the thing that makes a form feel stuck. */}
                    {row.generic_name && rowBlocked && (
                      <div style={{
                        marginTop: 9, padding: '7px 9px', borderRadius: 7,
                        fontSize: 11, lineHeight: 1.5,
                        background: '#FDF6EC', color: '#8A5510',
                        border: `1px solid ${BRAND.amber}55`,
                      }}>
                        {rowBadUnit
                          ? <>The dose unit needs a quantity, a space, then the
                            unit — <strong>500 mg</strong>.</>
                          : <>Every field is required before this can be saved.
                            Still needed: <strong>{rowMissing.join(', ')}</strong>.</>}
                      </div>
                    )}
                  </td>
                </tr>
    );
  };

  //  New rows only — an edited row is drawn where it already sits.
  const newDrafts = drafts.filter((d) => !d.id);
  const draftFor = (id) => drafts.find((d) => d.id != null && d.id === id);

  //  Date order, ties broken by id. This is the table the problem showed up on —
  //  489 rows over a two-week admission, where a row appended at the end was
  //  several screens past the fold. Drafts are typed at the top; on save the row
  //  settles onto its own dispensing day.
  const saved = rows
    .slice()
    .sort((a, b) => String(a.dispensed_at || '').localeCompare(String(b.dispensed_at || ''))
      || (Number(a.id) || 0) - (Number(b.id) || 0));
  const isEmpty = saved.length === 0 && drafts.length === 0;
  //  The export blocker, counted on the claim in front of the officer rather than
  //  discovered when NHIA rejects the batch.
  //  Counted by the SHAPE, not by mere presence. `!r.dose_unit` let a stored
  //  "250" through as answered — it is truthy — so the banner said 59 of 457
  //  while the rows carrying a quantity in the unit column went uncounted and
  //  would have been rejected alongside the blank ones.
  const missingDose = saved.filter((r) => !r.dose_unit
    || !DOSE_UNIT_RE.test(String(r.dose_unit).trim()));
  const inferredDose = saved.filter((r) => r.dose_unit_from === 'name');

  const dateCell = (row) => (
    <VisitDateSelect
      value={row.dispensed_at || dates[0]}
      dates={dates}
      style={cell}
      onChange={(d) => patch(row.key, 'dispensed_at', d)}
    />
  );

  // ── Moving a SAVED row to another day of the stay ──────────────────────────
  //  Changing the day used to mean opening the whole row for editing, finding the
  //  date among nine other fields and saving. On a four-night admission with ten
  //  drugs that is the commonest correction there is, so it happens here instead.
  //
  //  The PUT carries every field, not just the date: this endpoint replaces the
  //  row, so sending the date alone would blank the prescription. The values come
  //  from the row as it stands, so nothing but the day changes.
  const changeSavedDate = async (r, when) => {
    if (!when || when === String(r.dispensed_at || '').slice(0, 10)) return;
    setBusy(`date-${r.id}`); setError(null);
    try {
      const { data } = await axios.put(claimStoreMedication, {
        id: r.id,
        code: r.code || null,
        generic_name: r.generic_name,
        dosage_form: r.dosage_form || null,
        strength: r.strength || null,
        dose_unit: r.dose_unit || null,
        dose: r.dose || null,
        frequency: r.frequency || null,
        duration: r.duration || null,
        route: r.route || null,
        quantity: r.quantity,
        unit_price: r.unit_price,
        dispensed_at: when,
      }, { params: { id: claimId, source } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      //  The store refuses a duplicate of the same drug on the same day, which is
      //  exactly what moving one onto a day it is already on would create. That
      //  message is the useful one, so it is shown rather than replaced.
      setError(e?.response?.data?.message
        || 'Could not move that medication to another day.');
    } finally { setBusy(null); }
  };


  //  A field with its label above it. The draft row is a form now, so the inputs are
  //  a normal size instead of shrunk to fit a table column.
  const box = {
    width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: 12.5,
    border: `1px solid ${BRAND.green}`, borderRadius: 7, background: '#fff',
    color: BRAND.ink, outline: 'none', fontFamily: 'inherit',
  };
  const field = (label, control) => (
    <div>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4,
        textTransform: 'uppercase', color: BRAND.inkSoft, marginBottom: 3 }}>
        {label}
      </div>
      {control}
    </div>
  );


  return (
    <div>
      {/*  The dose unit is an NHIA XML validation, so a claim carrying a line
           without one will be rejected. Said here, on the claim, before export. */}
      {missingDose.length > 0 && (
        <div style={{
          marginBottom: 8, padding: '7px 10px', borderRadius: 7, fontSize: 11.5,
          background: BRAND.dangerSoft, color: BRAND.danger, border: '1px solid #fecaca',
        }}>
          <strong>{missingDose.length} of {saved.length}</strong>
          {' medication'}{missingDose.length === 1 ? ' has' : 's have'}
          {' no dose unit — NHIA requires one, so the claim would be rejected: '}
          {missingDose.slice(0, 3).map((r) => r.generic_name).join(', ')}
          {missingDose.length > 3 ? ` and ${missingDose.length - 3} more` : ''}.
        </div>
      )}
      {inferredDose.length > 0 && (
        <div style={{
          marginBottom: 8, padding: '6px 9px', borderRadius: 6, fontSize: 10.5,
          background: BRAND.amberSoft, color: BRAND.amber, border: '1px solid #fde68a',
        }}>
          {inferredDose.length} dose unit{inferredDose.length === 1 ? ' was' : 's were'}
          {' read from the medication name rather than recorded against the drug. '}
          Worth checking before export.
        </div>
      )}
      {catNote && (
        <div style={{
          marginBottom: 8, fontSize: 10.5, padding: '6px 9px', borderRadius: 6,
          background: BRAND.amberSoft, color: BRAND.amber, border: '1px solid #fde68a',
        }}>{catNote}</div>
      )}
      {error && (
        <div style={{
          marginBottom: 8, padding: '7px 10px', borderRadius: 7, fontSize: 11.5,
          background: BRAND.dangerSoft, color: BRAND.danger, border: '1px solid #fecaca',
        }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 780, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}>#</th>
              <th style={{ ...th, width: 96 }}>Code</th>
              <th style={th}>Medication</th>
              <th style={{ ...th, width: 64, textAlign: 'right' }}>Qty</th>
              <th style={{ ...th, width: 96, textAlign: 'right' }}>Total</th>
              <th style={{ ...th, width: 120 }}>Date</th>
              <th style={{ ...th, width: 76 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/*  A duplicate draft row turns red, so the message above names which
                 row it is talking about. */}
            {/*  BULK SAVE, only once there is more than one draft. With a single
                 row the button beside it is closer and clearer; the bar earns its
                 place at two and pays for itself at ten. */}
            {newDrafts.length > 1 && (() => {
              const ready = newDrafts.filter((d) => d.generic_name
                && !missingOf(d).length && !badDoseUnit(d));
              const working = busy === 'ALL';
              return (
                <tr>
                  <td colSpan={7} style={{
                    ...td, padding: '9px 10px', background: BRAND.greenSoft,
                    borderBottom: `1px solid ${BRAND.line}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                      flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: BRAND.inkSoft }}>
                        <strong>{newDrafts.length}</strong> medication rows open
                        {ready.length < newDrafts.length && (
                          <span style={{ color: '#8A5510' }}>
                            {' '}· {newDrafts.length - ready.length} still incomplete
                          </span>
                        )}
                      </span>
                      <div style={{ flex: 1 }} />
                      <button type="button" onClick={saveAll}
                        disabled={working || ready.length === 0}
                        title={ready.length
                          ? `Save the ${ready.length} finished row(s)`
                          : 'Fill in at least one row completely first'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '7px 13px', borderRadius: 8, fontSize: 12.5,
                          fontWeight: 800, fontFamily: 'inherit',
                          border: '1px solid transparent', color: '#fff',
                          cursor: working || !ready.length ? 'not-allowed' : 'pointer',
                          background: working || !ready.length
                            ? BRAND.inkFaint : BRAND.greenDark,
                        }}>
                        {working
                          ? <Loader2 size={13} className="spin" />
                          : <Save size={13} />}
                        {working ? 'Saving…' : `Save all ${ready.length}`}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })()}

            {newDrafts.map(draftTr)}

            {isEmpty && (
              <tr><td colSpan={7} style={{
                ...td, textAlign: 'center', color: BRAND.inkFaint, padding: '22px 10px',
              }}>No medications available</td></tr>
            )}

            {saved.map((r, i) => {
              //  Being edited? Draw the draft HERE, in this row's place.
              const d = draftFor(r.id);
              if (d) return draftTr(d, i);
              return (
              <tr key={r.id}>
                <td style={{ ...td, color: BRAND.inkFaint }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 700 }}>{r.code || '—'}</td>
                <td style={td}>
                  {r.generic_name}
                  {/*  Some facilities' claim files carry the medicine CODE and no
                       name at all, so this column read "COAMOXIN1" where a drug
                       name belongs. claimStore now resolves the code against this
                       facility's own store_pro catalogue — but a resolved name is
                       an interpretation of a code, not something the claim said,
                       and the two must not look identical on a claim being signed
                       off. Marked, not silently substituted. */}
                  {r.name_from === 'catalogue' && (
                    <span title="Matched from this facility's medication catalogue — the claim carried only the code"
                      style={{
                        marginLeft: 6, padding: '1px 5px', borderRadius: 4,
                        fontSize: 9, fontWeight: 800, verticalAlign: 'middle',
                        background: BRAND.greenSoft, color: BRAND.greenDark,
                      }}>
                      FROM CATALOGUE
                    </span>
                  )}
                  {r.name_from === 'code' && (
                    <span title="This code is not in the medication catalogue, so there is no name to show"
                      style={{
                        marginLeft: 6, padding: '1px 5px', borderRadius: 4,
                        fontSize: 9, fontWeight: 800, verticalAlign: 'middle',
                        background: BRAND.amberSoft, color: BRAND.amber,
                        border: '1px dashed #fcd34d',
                      }}>
                      CODE ONLY
                    </span>
                  )}
                  <span style={{ marginLeft: 6 }}>
                    {doseChip(r.dose_unit, r.dose_unit_from)}
                  </span>
                  <div style={{ fontSize: 10.5, color: BRAND.inkSoft, marginTop: 2 }}>
                    Rx: {rxLine(r) || r.unparsed_prescription || '—'}
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums' }}>{r.quantity}</td>
                <td style={{ ...td, textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums' }}>{ghs(r.total)}</td>
                <td style={td}>
                  <VisitDateSelect
                    value={r.dispensed_at}
                    dates={dates}
                    disabled={readOnly}
                    busy={busy === `date-${r.id}`}
                    ariaLabel={`Day for ${r.generic_name || r.code}`}
                    onChange={(d) => changeSavedDate(r, d)}
                  />
                </td>
                <td style={td}>
                  {!readOnly && (
                    <div style={{ display: 'flex', gap: 5 }}>
                      {/*  A PENCIL, because this opens the row for editing — a
                           disk icon reads as "save", which is what it did before
                           and what it does not do. */}
                      <button type="button" title="Edit this medication"
                        style={iconBtn()}
                        aria-label={`Edit ${r.generic_name}`}
                        onClick={() => editRow(r)}><Pencil size={13} /></button>
                      <button type="button" title="Remove" style={iconBtn('danger')}
                        aria-label={`Remove ${r.generic_name}`}
                        onClick={() => remove(r)}><Trash2 size={13} /></button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}

          </tbody>
        </table>
      </div>
    </div>
  );
}
